import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import CloseIcon from "@mui/icons-material/Close";

import Button from "./Button";
import IconButton from "./IconButton";
import Spinner from "./Spinner";
import ScopePicker from "./ScopePicker";
import SingleSensorPicker from "./SingleSensorPicker";
import {
  sendInvitation,
  putPendingAccessGrant,
  apiMessage,
  MEMBERS_QUERY_KEY,
  GRANTS_QUERY_KEY,
} from "../../services/people";
import {
  draftFromGrant,
  draftToGrantBody,
  grantsNothing,
  type GrantDraft,
} from "../../services/grantDraft";
import { getSensorGroups } from "../../pages/Events/unitEventsUtils";
import { useQuery } from "@tanstack/react-query";
import { useRoleCatalogue, invitableRoles, viewerTier } from "../../services/roles";
import { useViewer } from "../../services/viewer";

type Props = {
  open: boolean;
  onClose: () => void;
};

/**
 * Invite somebody into this tenant.
 *
 * The hand-rolled dialog shell every modal in this app uses -- `role="dialog"`,
 * `aria-modal`, Escape to close, backdrop click -- rather than MUI's Dialog, which
 * nothing here uses.
 *
 * **Roles come from the catalogue, never from a list in this file.** The dashboard
 * holds no role-name literal: the API owns role -> tier -> label, and `label` is what
 * a customer should read ("Administrator", "Manager", "User") while `name` stays the
 * authorization identifier nobody should see. The options are narrowed by the
 * viewer's own tier, mirroring `RoleCapabilities.MayAssign`, so a Manager is not shown
 * "Manager" as something to hand out.
 *
 * **It narrows, it does not enforce.** The API re-checks delegation on the invitation
 * itself (REQ-AUTH0-4), before the mail is sent, and a refusal is surfaced here rather
 * than assumed away. If the two ever disagree the API wins and the admin is told why.
 */
export default function InviteUserModal({ open, onClose }: Props) {
  const queryClient = useQueryClient();
  const { data: viewer } = useViewer();
  const catalogue = useRoleCatalogue();

  const [email, setEmail] = useState("");
  const [roleId, setRoleId] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);

  // Empty to begin with -- there is no existing grant to read for somebody who has
  // not been invited yet -- and cleared on each open beside the address below, for
  // the same reason: the last invitation's sensor must not be pre-ticked for the
  // next person. Unlike the member and pending editors, which seed from a stored
  // grant exactly once, this one has nothing to seed from.
  const [draft, setDraft] = useState<GrantDraft>(() => draftFromGrant(undefined));

  // Reported separately from the invitation's own failure, because the two have
  // very different remedies: a failed invitation can simply be re-sent, while a
  // failed grant means somebody HAS been invited and their access still needs
  // setting from their row.
  const [grantFailed, setGrantFailed] = useState<string | null>(null);

  // A previous invitation's address and error must not greet the next one.
  useEffect(() => {
    if (open) {
      setEmail("");
      setRoleId("");
      setSending(false);
      setError(null);
      setSentTo(null);
      setGrantFailed(null);
      setDraft(draftFromGrant(undefined));
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !sending) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose, sending]);

  const properties = useQuery({
    queryKey: ["admin", "properties"],
    queryFn: async () => {
      const response = await getSensorGroups();
      return ((response?.data ?? []) as { id: string; name: string }[]).map((g) => ({
        buildingId: g.id,
        name: g.name,
      }));
    },
    staleTime: 5 * 60_000,
    retry: false,
    // Nothing to pick while the dialog is shut, and the People page mounts this
    // closed, so an unconditional query would fetch the sensors on every visit.
    enabled: open,
  });

  if (!open) return null;

  const callerTier = viewerTier(
    (viewer?.roles ?? []).map((r) => r.name),
    catalogue.data
  );
  const options = invitableRoles(catalogue.data, callerTier);
  const chosen = options.find((r) => r.id === roleId);

  // Sensor access CAN be chosen here now, and this comment used to say the
  // opposite: "structural rather than a missing feature", because a grant is keyed
  // on user_id and an invitee has none. That stopped being true when pending grants
  // landed (REQ-ACL-23) -- a grant now waits under a key built from the invited
  // address and moves onto the account at their first sign-in. The dialog went on
  // telling admins to come back later for a fortnight after the thing it apologised
  // for had been built.
  const chosenIsSingleSensorTier = chosen?.tier === "User";

  const choseAnySensor = !grantsNothing(draft);

  const canSend =
    email.trim().length > 0 && !sending && catalogue.isSuccess;

  const send = async () => {
    setSending(true);
    setError(null);
    try {
      const sent = email.trim();
      const result = await sendInvitation(sent, roleId ? [roleId] : []);

      // The grant goes on the invitation the send just named. Reading the list back
      // to find it would be racy -- two invitations to one address cannot be told
      // apart -- which is why the API returns the id (REQ-ACL-23).
      //
      // AFTER the invitation, never before: a grant written for an invitation that
      // then failed to send would wait under an address nobody had invited, and
      // apply to whoever next arrived there.
      if (choseAnySensor) {
        if (result?.invitation_id) {
          try {
            await putPendingAccessGrant(result.invitation_id, draftToGrantBody(draft));
            queryClient.invalidateQueries({ queryKey: GRANTS_QUERY_KEY });
          } catch (grantError) {
            // The invitation HAS gone out, so this is reported beside the
            // confirmation rather than thrown: telling them it failed would be
            // false, and swallowing it would leave somebody arriving with access
            // nobody chose.
            setGrantFailed(
              apiMessage(
                grantError,
                "We couldn't save their sensor access. You can set it from their row."
              )
            );
          }
        } else {
          // A family invitation names no invitation, so there is nothing to attach
          // a grant to. Said rather than silently dropped.
          setGrantFailed(
            "We couldn't save their sensor access. You can set it from their row."
          );
        }
      }

      // The address WE sent, not the one that came back. The confirmation is
      // about an invitation that has already gone out, so it must not depend on
      // the response's shape -- it did, and an unwrapped envelope left `email`
      // undefined, so `sentTo` stayed falsy and the dialog never switched off
      // the form. The invitation had been sent.
      setSentTo(result?.email || sent);
      // The People list is the screen an admin checks after inviting somebody.
      // An organization invitation does not create a member until it is accepted,
      // so this refresh may legitimately show no new row -- which is why the
      // success copy says what to expect rather than letting the list imply it.
      queryClient.invalidateQueries({ queryKey: MEMBERS_QUERY_KEY });
    } catch (e) {
      setError(
        apiMessage(
          e,
          "We couldn't send the invitation. Nothing was sent, so you can try again."
        )
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Invite someone"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={() => !sending && onClose()}
    >
      <div
        className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-card bg-white text-ink p-6 shadow-depth border border-black/5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-1">
          <h2 className="text-xl font-semibold">Invite someone</h2>
          <IconButton label="Close" onClick={() => !sending && onClose()}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>

        {sentTo ? (
          <div className="mt-4 space-y-4">
            <p className="text-sm">
              An invitation is on its way to{" "}
              <span className="font-semibold">{sentTo}</span>.
            </p>
            {/* What happens next, because the People list will not show them yet
                and an admin who does not know that will read it as a failure. */}
            <p className="text-ink-2 text-sm">
              They'll appear in this list once they accept.
            </p>
            {grantFailed ? (
              <p className="text-alert-critical text-sm" role="alert">
                {grantFailed}
              </p>
            ) : choseAnySensor ? (
              <p className="text-ink-2 text-sm">
                Their sensor access is saved and applies as soon as they sign in.
              </p>
            ) : (
              <p className="text-ink-2 text-sm">
                You can set their sensor access from their row at any time.
              </p>
            )}
            <div className="flex justify-end">
              <Button onClick={onClose}>Done</Button>
            </div>
          </div>
        ) : (
          <>
            <p className="text-ink-2 text-sm mt-1">
              They will get an email inviting them to{" "}
              {viewer?.tenant_name || "this organization"}.
            </p>

            <div className="mt-4 space-y-4">
              <label className="block">
                <span className="block text-sm font-medium mb-1">
                  Email address
                </span>
                <input
                  type="email"
                  autoFocus
                  value={email}
                  disabled={sending}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    // A refusal names the address it refused. Once that address
                    // has been edited the message is about somebody else, and
                    // leaving it up states something untrue about what is now in
                    // the field -- seen in the preview: "taken@example.com is
                    // already a member" sitting under "colleague@example.com".
                    setError(null);
                  }}
                  placeholder="colleague@example.com"
                  className="w-full rounded-control border border-black/15 bg-white px-3 py-2 text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30"
                />
              </label>

              <label className="block">
                <span className="block text-sm font-medium mb-1">Role</span>

                {catalogue.isPending ? (
                  <div className="flex items-center gap-2 py-2">
                    <Spinner size={16} color="currentColor" />
                    <span className="text-ink-2 text-sm">Loading roles…</span>
                  </div>
                ) : catalogue.isError ? (
                  <p className="text-alert-critical text-sm py-2">
                    We couldn't load the roles, so there is nothing to choose from.
                    Reload to try again.
                  </p>
                ) : options.length === 0 ? (
                  // Not the same as "no roles exist". A Manager may only invite
                  // below Manager, so if the tenant has no such role there is
                  // genuinely nobody they can invite, and saying why beats an
                  // empty dropdown.
                  <p className="text-ink-2 text-sm py-2">
                    There are no roles you can assign. Only an Administrator can
                    invite someone at your own level.
                  </p>
                ) : (
                  <>
                    <select
                      value={roleId}
                      disabled={sending}
                      onChange={(e) => {
                        setRoleId(e.target.value);
                        // Same reason: a refusal may have been about the role,
                        // and changing it makes the message stale either way.
                        setError(null);
                      }}
                      className="w-full rounded-control border border-black/15 bg-white px-3 py-2 text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30"
                    >
                      {/* Choosable, unlike the sensor picker's placeholder: a
                          member with no role is a real state the API accepts,
                          and the tier can be set after they accept. */}
                      <option value="">No role yet</option>
                      {options.map((r) => (
                        <option key={r.id} value={r.id}>
                          {r.label}
                        </option>
                      ))}
                    </select>

                    {chosen?.summary && (
                      <p className="text-ink-2 text-sm mt-2">{chosen.summary}</p>
                    )}
                  </>
                )}
              </label>

              {/* Sensor access, chosen now rather than after they accept. The copy
                  here used to say "you'll add their sensor access after they
                  accept", which outlived the limitation it described. */}
              <div className="space-y-2">
                <span className="block text-sm font-medium">
                  Sensor access <span className="text-ink-3">(optional)</span>
                </span>

                {properties.isError ? (
                  <p className="text-alert-critical text-sm">
                    We couldn't load the sensors. You can set their access from their
                    row once they're invited.
                  </p>
                ) : chosenIsSingleSensorTier ? (
                  <SingleSensorPicker
                    value={draft}
                    properties={properties.data ?? []}
                    onChange={setDraft}
                    disabled={sending}
                  />
                ) : (
                  <ScopePicker
                    value={draft}
                    properties={properties.data ?? []}
                    onChange={setDraft}
                    disabled={sending}
                  />
                )}

                <p className="text-ink-2 text-sm">
                  {choseAnySensor
                    ? "This applies as soon as they sign in."
                    : "Leave this if you'd rather choose later; you can set it from their row."}
                </p>
              </div>

              {error && (
                <p className="text-alert-critical text-sm" role="alert">
                  {error}
                </p>
              )}

              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={onClose} disabled={sending}>
                  Cancel
                </Button>
                <Button onClick={send} disabled={!canSend}>
                  {sending ? "Sending…" : "Send invitation"}
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
